import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

function uniqueSorted(nums) {
  return [...new Set(nums)].sort((a, b) => a - b);
}

function factorFromRoot(root) {
  if (root === 0) return "x";
  if (root > 0) return `(x-${root})`;
  return `(x+${Math.abs(root)})`;
}

function rootsListLatex(roots) {
  if (roots.length === 0) return "\\varnothing";
  const terms = roots.map((r) => (r < 0 ? `(${r})` : `${r}`));
  if (terms.length === 1) return terms[0];
  if (terms.length === 2) return `${terms[0]}\\; \\text{oraz}\\; ${terms[1]}`;
  return `${terms.slice(0, -1).join(",\\; ")}\\; \\text{oraz}\\; ${terms[terms.length - 1]}`;
}

function rootsListText(roots) {
  const terms = roots.map((r) => (r < 0 ? `(${r})` : `${r}`));
  if (terms.length === 0) return "brak";
  if (terms.length === 1) return terms[0];
  if (terms.length === 2) return `${terms[0]} oraz ${terms[1]}`;
  return `${terms.slice(0, -1).join(", ")} oraz ${terms[terms.length - 1]}`;
}

function linearFactorEquationFromRoot(root) {
  if (root === 0) return "x=0";
  if (root > 0) return `x-${root}=0`;
  return `x+${Math.abs(root)}=0`;
}

function countPhrase(count) {
  if (count === 1) return "jedno rozwiązanie";
  if (count === 2) return "dwa rozwiązania";
  if (count === 3) return "trzy rozwiązania";
  return "cztery rozwiązania";
}

function optionData(roots) {
  const normalized = uniqueSorted(roots);
  const c = normalized.length;
  return {
    roots: normalized,
    count: c,
    text: `${countPhrase(c)}: ${rootsListText(normalized)}.`,
    latex: `${countPhrase(c)}:\\; ${rootsListLatex(normalized)}`,
  };
}

function renderRootsInline(roots) {
  if (!Array.isArray(roots) || roots.length === 0) return <>brak</>;

  return (
    <>
      {roots.map((r, idx) => {
        const isLast = idx === roots.length - 1;
        return (
          <React.Fragment key={`${r}-${idx}`}>
            {idx > 0 && (isLast ? " oraz " : ", ")}
            <InlineMath math={r < 0 ? `(${r})` : `${r}`} />
          </React.Fragment>
        );
      })}
    </>
  );
}

function renderOptionSentence(option) {
  const roots = option?.roots || [];
  const count = Number.isInteger(option?.count) ? option.count : roots.length;
  return (
    <>
      {countPhrase(count)}: {renderRootsInline(roots)}.
    </>
  );
}

function pickDistinctFromPool(pool, forbidden = []) {
  const ban = new Set(forbidden);
  const candidates = pool.filter((v) => !ban.has(v));
  return pick(candidates);
}

function normalizeOption(roots, targetCount) {
  const normalized = uniqueSorted(roots);
  if (normalized.length !== targetCount) return null;
  return normalized;
}

function generateMay2025OriginalTask() {
  const optionRoots = [
    [-3, 0],
    [-3, 2],
    [-5, -3, 0],
    [-5, -3, 0, 5],
  ];
  const options = optionRoots.map((roots, index) => ({
    ...optionData(roots),
    label: LETTERS[index],
    isCorrect: index === 0,
  }));

  return {
    type: "MaturaFactoredRealRootsClosedTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 7,
    },
    data: {
      eqLatex: "2x(x+3)(x^2+25)=0",
      pattern: "two",
      hasXFactor: true,
      leadCoeff: 2,
      options,
      correctIndex: 0,
      correctRoots: [-3, 0],
      linearRoots: [-3, 0],
      quadraticQ: 25,
      factorNote: "x^2+25=0",
      extraRoots: "\\varnothing",
      maxPoints: 1,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025OriginalTask();
  }

  const intPool = [-6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6];
  const squareRoots = [2, 3, 4, 5];

  while (true) {
    const pattern = pick(["two", "twoNoX", "three", "four"]);
    const a = randInt(2, 5);

    let eqLatex = "";
    let correctRoots = [];
    let distractorRoots = [];
    let explanation = {};

    if (pattern === "two") {
      const r1 = pick(intPool);
      const s = pick(squareRoots);
      const q = s * s; // x^2 + q -> brak rozwiązań rzeczywistych
      if (Math.abs(r1) === a || s === a) continue; // unikamy podpowiedzi ze współczynnika

      eqLatex = `${a}x${factorFromRoot(r1)}(x^2+${q})=0`;
      correctRoots = uniqueSorted([0, r1]);

      // zmyłka: współczynnik stojący przy x bywa mylony z rozwiązaniem
      const forbiddenTwo = [0, r1, -s, s];
      let coeffTrap = [a, -a].find((v) => !forbiddenTwo.includes(v));
      if (coeffTrap === undefined) {
        coeffTrap = pickDistinctFromPool(intPool, forbiddenTwo);
      }
      const twoPlusTwoMode = Math.random() < 0.5; // naprzemiennie: 2,2,3,4 oraz 2,3,3,4
      const wrongA = twoPlusTwoMode
        ? normalizeOption([r1, coeffTrap], 2)
        : normalizeOption([0, r1, coeffTrap], 3);
      const wrongB = normalizeOption([-s, r1, 0], 3);
      const wrongC = normalizeOption([-s, r1, 0, s], 4);

      if (!wrongA || !wrongB || !wrongC) continue;
      distractorRoots = [wrongA, wrongB, wrongC];

      explanation = {
        linearRoots: uniqueSorted([0, r1]),
        quadraticQ: q,
        factorNote: `x^2+${q}=0`,
        extraRoots: "\\varnothing",
      };
    } else if (pattern === "twoNoX") {
      // wariant bez "x" jako czynnika, ale z liczbą przed nawiasami
      const r1 = pick(intPool);
      const r2 = pickDistinctFromPool(intPool, [r1]);
      const s = pick(squareRoots);
      const q = s * s;
      if (Math.abs(r1) === s || Math.abs(r2) === s) continue;
      if (r2 === -r1) continue; // unikamy zbyt oczywistego rozbicia (x-a)(x+a)
      if (Math.abs(r1) === a || Math.abs(r2) === a || s === a) continue;

      eqLatex = `${a}${factorFromRoot(r1)}${factorFromRoot(r2)}(x^2+${q})=0`;
      correctRoots = uniqueSorted([r1, r2]);

      const trapNoX = pickDistinctFromPool(intPool, [0, r1, r2]);
      const twoPlusTwoMode = Math.random() < 0.5; // naprzemiennie: 2,2,3,4 oraz 2,3,3,4
      const wrongA = twoPlusTwoMode
        ? normalizeOption([r1, trapNoX], 2)
        : normalizeOption([0, r1, r2], 3);
      const wrongB = normalizeOption([trapNoX, r1, r2], 3);
      const wrongC = normalizeOption([-s, r1, r2, s], 4);

      if (!wrongA || !wrongB || !wrongC) continue;
      distractorRoots = [wrongA, wrongB, wrongC];

      explanation = {
        linearRoots: uniqueSorted([r1, r2]),
        quadraticQ: q,
        factorNote: `x^2+${q}=0`,
        extraRoots: "\\varnothing",
      };
    } else if (pattern === "three") {
      const r1 = pick(intPool);
      const r2 = pickDistinctFromPool(intPool, [r1]);
      const s = pick(squareRoots);
      const q = s * s; // brak realnych z x^2+q
      if (r2 === -r1) continue; // unikamy zbyt oczywistego rozbicia (x-a)(x+a)
      if (Math.abs(r1) === s || Math.abs(r2) === s) continue;
      if (Math.abs(r1) === a || Math.abs(r2) === a || s === a) continue;

      eqLatex = `${a}x${factorFromRoot(r1)}${factorFromRoot(r2)}(x^2+${q})=0`;
      correctRoots = uniqueSorted([0, r1, r2]);

      const forbiddenThree = [0, r1, r2, -s, s];
      let coeffTrap = [a, -a].find((v) => !forbiddenThree.includes(v));
      if (coeffTrap === undefined) {
        coeffTrap = pickDistinctFromPool(intPool, forbiddenThree);
      }
      const twoPlusTwoMode = Math.random() < 0.5; // naprzemiennie: 2,2,3,4 oraz 2,3,3,4
      const wrongA = normalizeOption([0, r1], 2);
      const wrongB = twoPlusTwoMode
        ? normalizeOption([r1, coeffTrap], 2)
        : normalizeOption([0, r1, coeffTrap], 3);
      // przy x^2+q błędem bywa traktowanie ±sqrt(q) jako rozwiązań
      const fakeS = pick([-s, s]);
      const wrongC = normalizeOption([0, r1, r2, fakeS], 4);

      if (!wrongA || !wrongB || !wrongC) continue;
      distractorRoots = [wrongA, wrongB, wrongC];

      explanation = {
        linearRoots: uniqueSorted([0, r1, r2]),
        quadraticQ: q,
        factorNote: `x^2+${q}=0`,
        extraRoots: "\\varnothing",
      };
    } else {
      const r1 = pick(intPool);
      const s = pick(squareRoots);
      if (Math.abs(r1) === s) continue; // unikamy duplikatu rozwiązań
      if (Math.abs(r1) === a) continue; // unikamy podpowiedzi ze współczynnika

      eqLatex = `${a}x${factorFromRoot(r1)}(x^2-${s * s})=0`;
      correctRoots = uniqueSorted([0, r1, -s, s]);

      const twoPlusTwoMode = Math.random() < 0.5; // naprzemiennie: 2,2,3,4 oraz 2,3,3,4
      const wrongA = normalizeOption([0, r1], 2);
      const coeffTrap = pickDistinctFromPool(intPool, [0, r1, -s, s]);
      const signTrap = pick([-s, s]);
      const wrongB = twoPlusTwoMode
        ? normalizeOption([r1, coeffTrap], 2)
        : normalizeOption([0, r1, signTrap], 3);
      const wrongC = normalizeOption([0, r1, coeffTrap], 3);

      if (!wrongA || !wrongB || !wrongC) continue;
      distractorRoots = [wrongA, wrongB, wrongC];

      explanation = {
        linearRoots: uniqueSorted([0, r1]),
        factorNote: `x^2-${s * s}=0 \\Rightarrow x=\\pm ${s}`,
        extraRoots: `${rootsListLatex(uniqueSorted([-s, s]))}`,
      };
    }

    const correct = {
      ...optionData(correctRoots),
      isCorrect: true,
    };
    const wrong = distractorRoots.map((roots) => ({
      ...optionData(roots),
      isCorrect: false,
    }));

    // pewność unikalności odpowiedzi
    const latexSet = new Set([correct.latex, ...wrong.map((w) => w.latex)]);
    if (latexSet.size !== 4) continue;

    const options = [correct, ...wrong]
      .sort((x, y) => x.count - y.count)
      .map((o, idx) => ({
        ...o,
        label: LETTERS[idx],
      }));

    const correctIndex = options.findIndex((o) => o.isCorrect);
    if (correctIndex < 0) continue;

    return {
      type: "MaturaFactoredRealRootsClosedTask",
      data: {
        eqLatex,
        pattern,
        hasXFactor: pattern !== "twoNoX",
        leadCoeff: a,
        options,
        correctIndex,
        correctRoots,
        ...explanation,
      },
    };
  }
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Równanie <InlineMath math={data.eqLatex} /> w zbiorze liczb rzeczywistych ma
        dokładnie:
      </p>

      <div style={{ marginTop: 8 }}>
        {data.options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 6 }}>
            <InlineMath math={`${opt.label}.`} /> {renderOptionSentence(opt)}
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = data.options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      text: opt.text,
      roots: opt.roots,
      count: opt.count,
    });
  };

  const letterOptions = data.options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return (
    <span>
      <InlineMath math={`${correct.label}.`} /> {renderOptionSentence(correct)}
    </span>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  if (Array.isArray(userAnswer.roots) && Number.isInteger(userAnswer.count)) {
    return (
      <span>
        <InlineMath math={`${userAnswer.label}.`} />{" "}
        {renderOptionSentence({ roots: userAnswer.roots, count: userAnswer.count })}
      </span>
    );
  }

  if (userAnswer.text) {
    return (
      <span>
        <InlineMath math={`${userAnswer.label}.`} /> {userAnswer.text}
      </span>
    );
  }

  if (userAnswer.latex) {
    return <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />;
  }

  return (
    <span>
      <InlineMath math={`${userAnswer.label}.`} />
    </span>
  );
}

export function renderExplanation({ data }) {
  const {
    eqLatex,
    correctRoots,
    factorNote,
    extraRoots,
    pattern,
    linearRoots = [],
    quadraticQ,
    hasXFactor,
    leadCoeff,
  } = data;
  const firstStepLatex =
    pattern === "twoNoX"
      ? `${eqLatex} \\Rightarrow \\text{co najmniej jeden czynnik jest równy }0`
      : `${eqLatex} \\Rightarrow \\text{co najmniej jeden czynnik jest równy }0`;

  const linearEqLatex =
    linearRoots.length > 0
      ? linearRoots.map((r) => linearFactorEquationFromRoot(r)).join("\\; \\lor\\; ")
      : "\\varnothing";
  const linearRootsSetLatex =
    linearRoots.length > 0
      ? linearRoots.map((r) => (r < 0 ? `(${r})` : `${r}`)).join(",\\; ")
      : "\\varnothing";
  const noRealQuadratic = extraRoots === "\\varnothing";

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Zauważamy, że równanie ma postać iloczynu
        czynników:
      </p>
      <BlockMath math={firstStepLatex} />
      <p>
        Iloczyn jest równy zero wtedy i tylko wtedy, gdy przynajmniej jeden
        czynnik jest równy zero.
      </p>
      {hasXFactor && (
        <p>
          <strong>Ważne.</strong> Czynnika z samotnym <InlineMath math="x" />{" "}nie pomijamy.
          On również może wyzerować cały iloczyn: <InlineMath math={`${leadCoeff}x=0\\Rightarrow x=0`} />.
        </p>
      )}

      <p>
        <strong>Krok 2.</strong> Rozwiązujemy równania z kolejnych czynników:
      </p>
      <ul style={{ paddingLeft: "1.5rem" }}>
        {linearRoots.map((r, idx) => {
          const eq =
            r === 0 && hasXFactor
              ? `${leadCoeff}x=0`
              : linearFactorEquationFromRoot(r);
          return (
            <li key={`lin-${idx}`} style={{ marginBottom: "0.35rem" }}>
              <BlockMath math={`${eq}\\Rightarrow x=${r < 0 ? `(${r})` : r}`} />
            </li>
          );
        })}
        <li style={{ marginBottom: "0.35rem" }}>
          <BlockMath math={factorNote} />
          {noRealQuadratic ? (
            <p style={{ textAlign: "center" }}>
              <InlineMath math={`x^2+${quadraticQ}=0\\Rightarrow x^2=-${quadraticQ}.`} />{" "}
              Kwadrat liczby rzeczywistej nie może być ujemny, więc brak
              rozwiązań rzeczywistych.
            </p>
          ) : (
            <BlockMath math={`\\Rightarrow x\\in\\{${correctRoots.filter(root => !linearRoots.includes(root)).join(",\\; ")}\\}`} />
          )}
        </li>
      </ul>

      <p>
        <strong>Krok 3.</strong> Zbiór rozwiązań rzeczywistych:
      </p>
      <BlockMath math={`\\{${correctRoots.join(",\\; ")}\\}`} />
      <p style={{ textAlign: "center" }}>
        Liczba rozwiązań: <InlineMath math={`${correctRoots.length}`} />
      </p>
    </div>
  );
}
